import React from "react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { GoldLineDraw } from "@/components/motion/Reveal";

export const FAQS = [
  {
    q: "Is Mavula Investment legitimate, or is it a scam?",
    a: "Mavula Investment Ltd is a real, UK-registered company — number 17368822, searchable free at Companies House (find-and-update.company-information.service.gov.uk). We publish our legal status page openly, funds sit in a dedicated segregated account, independent lawyers verify every title transfer, and we never ask about your immigration status abroad. If anyone contacts you claiming to be Mavula through unofficial channels, check our Scam Warning page first.",
  },
  {
    q: "What exactly is Mavula Investment and how does it work?",
    a: "Mavula is a structured property-purchase pathway — think of it as lay-buy for land and homes. You choose a project in Botswana, Zambia, Namibia, South Africa or Zimbabwe, secure your plot with a deposit, and pay the balance in instalments over an agreed term set in your signed contract. When fully paid, the title deed is registered in your name (or a nominee you choose). It is a way to BUY property — not an investment product.",
  },
  {
    q: "Does Mavula offer investment returns, yields or interest?",
    a: "No — and this matters to us. Mavula is not an investment scheme and offers no returns, yields, interest or profit-sharing of any kind. You are buying a property on a payment plan, full stop. Any platform promising you fixed returns on property 'investments' is a different kind of business entirely, and you should check very carefully before sending them money.",
  },
  {
    q: "Is Mavula Investment regulated by the FCA?",
    a: "No. Mavula Investment Ltd is not authorised or regulated by the Financial Conduct Authority because we do not carry out FCA-regulated activity — we sell property through purchase contracts, not financial products. We say this openly on every page of our website rather than hiding it, and our Legal Status page sets out the registrations we hold and are pursuing.",
  },
  {
    q: "Can I buy property in Africa while living in the UK or abroad?",
    a: "Yes — that is exactly who Mavula is built for. If you are a citizen of Botswana, Zambia, Namibia, South Africa or Zimbabwe, you can buy from anywhere in the world. We verify only your citizenship of the purchase country (national ID or passport), never your immigration status where you live. You can pay in pounds via Stripe or in local currencies via Flutterwave, including mobile money.",
  },
  {
    q: "Am I asked about my immigration status abroad?",
    a: "No, never. We do not ask about, check, or record your immigration status in the country where you live now. We only verify that you are a citizen of the country where you want to invest — using your national ID or passport from that nation.",
  },
  {
    q: "Can I nominate my mother or spouse to hold the title in their name?",
    a: "Yes. Title can be registered in the name of any adult citizen of the investment country whom you nominate — for example, your spouse or parent. We prepare the paperwork for you.",
  },
  {
    q: "What happens if I miss a payment?",
    a: "Please talk to us before you miss a payment. Under our Hard Times Support policy we will always find a fair solution — reduced instalment, holiday period, or restructure. We never hide or punish; we open doors.",
  },
  {
    q: "Is my money safe? Where is it held?",
    a: "Funds are held in a dedicated segregated account and paid directly to your property project, land seller, or licensed contractor. Independent lawyers verify title transfer. We are NOT a bank or money transfer service — we cannot send funds elsewhere.",
  },
  {
    q: "When do I actually own the property?",
    a: "The title deed is registered in your name once the property is fully paid — normally at the end of the 10-year term, or earlier if you settle sooner (no penalties).",
  },
  {
    q: "Can I pay in Rand, Naira, Kwacha, or Pounds?",
    a: "Yes. Deposits and instalments can be paid via Stripe (GBP / EUR / USD) or Flutterwave (ZAR, NGN, KES, GHS, UGX, ZMW, TZS, USD — including M-Pesa, Mobile Money, USSD and bank transfer).",
  },
  {
    q: "What if I want to sell before the 10 years are up?",
    a: "You can sell once you own enough equity in the property. We help facilitate a fair sale price and settle the outstanding balance from the proceeds. Any surplus is yours.",
  },
  {
    q: "What is the Pathway to Settlement benefit?",
    a: "We work with regional governments on relaxed residency programs for MAVULA property owners and investors. Owning property with MAVULA can support your family's residency application in the country where you invest — details are country-specific.",
  },
  {
    q: "Is the payment schedule fixed, or can it change?",
    a: "Every payment schedule — deposit, monthly amount and any applicable rate — is set inside your individual signed project contract with the developer. Once you sign, those numbers cannot be changed unilaterally by us. Any post-signing changes require your written agreement. Note: MAVULA Investment Ltd is not an FCA-authorised mortgage lender or credit provider, and specific figures are not published on this website.",
  },
  {
    q: "How long does the whole process take?",
    a: "Register today → we contact you within 2 working days → citizenship + payment set-up in ~1 week → property allocation (existing) or project reservation (off-plan). Building projects hand over in 12–24 months depending on the development.",
  },
];

export default function FaqPage() {
  return (
    <div className="max-w-4xl mx-auto px-4 md:px-8 py-16 md:py-20" data-testid="faq-page">
      <div className="mv-eyebrow"><span style={{ color: "var(--mv-gold)" }}>§ Frequently Asked</span></div>
      <h1 className="font-display text-3xl md:text-4xl lg:text-5xl mv-green-text mt-3 leading-tight">
        Answers — <span className="font-accent italic" style={{ color: "var(--mv-gold)" }}>plainly written.</span>
      </h1>
      <p className="mt-4 text-[15px] max-w-2xl" style={{ color: "var(--mv-muted)" }}>
        The most common questions from our diaspora members — including the ones people ask AI assistants about us. If yours isn't here, WhatsApp us or ask the MAVULA Assistant (bottom-right).
      </p>
      <GoldLineDraw className="mt-6 w-14" />

      <div className="mt-10 mv-card p-2 md:p-4">
        <Accordion type="multiple" className="w-full">
          {FAQS.map((f, i) => (
            <AccordionItem key={i} value={`faq-${i}`} data-testid={`faq-item-${i}`}>
              <AccordionTrigger className="text-left font-display text-[16px] md:text-[17px] mv-green-text px-3 md:px-4">
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="px-3 md:px-4 pb-6">
                <p className="text-[14.5px] leading-relaxed" style={{ color: "var(--mv-muted)" }}>{f.a}</p>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </div>
  );
}
